import type { CalculatorField, CalculatorFieldValues } from 'components/Widget/shared/types';
import type {
  ExemptionQuestion,
  PaycheckExemptions,
  PaycheckSegmentKey,
  PayFrequency,
  PayFrequencyOption,
  PayType,
  PayTypeOption,
  TaxYear,
  YesNoOption,
} from './types';

export const TITLE = 'Paycheck Calculator';

export const WEEKS_PER_YEAR = 52;

export const PAY_FREQUENCY_OPTIONS: readonly PayFrequencyOption[] = [
  { key: 'weekly', label: 'Weekly', periodsPerYear: WEEKS_PER_YEAR },
  { key: 'biweekly', label: 'Bi-weekly', periodsPerYear: 26 },
  { key: 'semimonthly', label: 'Semi-monthly', periodsPerYear: 24 },
  { key: 'monthly', label: 'Monthly', periodsPerYear: 12 },
];

// A closed set, so a key that misses is a coding error rather than user input.
export const getPayFrequency = (key: PayFrequency): PayFrequencyOption => {
  const option = PAY_FREQUENCY_OPTIONS.find(frequency => frequency.key === key);

  if (!option) throw new Error(`Unsupported pay frequency: ${key}`);

  return option;
};

/**
 * Brackets and standard deduction from IRS Rev. Proc. 2025-32 (sections 4.01 and 4.14); the wage
 * base from the SSA contribution and benefit base. All three are re-indexed every year.
 */
const TAX_YEAR_2026: TaxYear = {
  year: 2026,
  standardDeduction: { single: 16100 },
  brackets: {
    single: [
      { floor: 0, rate: 0.1 },
      { floor: 12400, rate: 0.12 },
      { floor: 50400, rate: 0.22 },
      { floor: 105700, rate: 0.24 },
      { floor: 201775, rate: 0.32 },
      { floor: 256225, rate: 0.35 },
      { floor: 640600, rate: 0.37 },
    ],
  },
  socialSecurityWageBase: 184500,
};

export const CURRENT_TAX_YEAR = TAX_YEAR_2026;

export const SOCIAL_SECURITY_RATE = 0.062;

export const MEDICARE_RATE = 0.0145;

export const ADDITIONAL_MEDICARE_RATE = 0.009;

/**
 * Statutory and not indexed, so it lives outside the tax year. It is the employer-withholding
 * threshold, which is why it does not vary by filing status the way the liability does.
 */
export const ADDITIONAL_MEDICARE_THRESHOLD = 200000;

/** Marks every string Legal has yet to supply, so it cannot pass for approved copy in a QA screenshot. */
const PLACEHOLDER = '[PLACEHOLDER COPY - PENDING LEGAL]';

export const DISCLAIMER =
  'This calculator is for educational purposes only and is not intended to offer any tax, legal, financial, or investment advice.';

export const FORM_LABEL = 'Paycheck calculator';

export const PAY_FREQUENCY_FIELD: CalculatorField = { label: 'How often are you paid?' };

export const PAY_TYPE_LEGEND = 'How are you paid?';

export const PAY_TYPE_OPTIONS: readonly PayTypeOption[] = [
  { key: 'hourly', label: 'Hourly' },
  { key: 'salary', label: 'Salary' },
];

export const DEFAULT_PAY_TYPE: PayType = 'hourly';

export const getPayType = (key: PayType): PayTypeOption => {
  const option = PAY_TYPE_OPTIONS.find(payType => payType.key === key);

  if (!option) throw new Error(`Unsupported pay type: ${key}`);

  return option;
};

export const DEFAULT_HOURLY_WAGE = '25';

/** Design's greyed defaults; `valueOrPlaceholder` makes Calculate fall back to these same numbers. */
export const HOURLY_WAGE_FIELD: CalculatorField = {
  label: 'Hourly wage',
  hint: 'An hourly wage is a set amount of money an employee is paid for each hour of work they perform.',
  placeholder: DEFAULT_HOURLY_WAGE,
};

export const HOURLY_WAGE_SUFFIX = '/ hr';

export const WEEKLY_HOURS_FIELD: CalculatorField = {
  label: 'Average weekly hours',
  hint: 'The number of hours an employee works per week, not including overtime, typically calculated over a rolling weekly period.',
  placeholder: '40',
};

export const ANNUAL_SALARY_FIELD: CalculatorField = {
  label: 'Gross annual salary',
  hint: 'The total amount of money an employee earns in one year before any taxes or deductions are taken out.',
  placeholder: '60,000',
};

export const ANNUAL_SALARY_SUFFIX = '/ year';

export const OVERTIME_SWITCH_LABEL = 'Overtime pay';

export const OVERTIME_MULTIPLIER = 1.5;

/** No placeholder here: it is 1.5x whatever the hourly wage currently resolves to, built at render and Calculate. */
export const OVERTIME_WAGE_FIELD: CalculatorField = { label: 'Overtime hourly wage', hint: PLACEHOLDER };

export const OVERTIME_HOURS_FIELD: CalculatorField = {
  label: 'Average overtime weekly hours',
  hint: PLACEHOLDER,
  placeholder: '5',
};

/** Every hour in a week, the only ceiling a weekly figure has. */
export const MAX_WEEKLY_HOURS = 168;

/** Two, so a timesheet's quarter hours (37.25) survive; one decimal could not hold them. */
export const WEEKLY_HOURS_FRACTION_DIGITS = 2;

/** Holds the worst case, 168 hours paid monthly, to a gross the result column can fit (about $7.3M). */
export const MAX_HOURLY_WAGE = 9999.99;

/** Room for 1.5x the top hourly wage, so the greyed overtime default never fails validation on its own. */
export const MAX_OVERTIME_WAGE = 14999.99;

/** Still clears the top federal bracket and both FICA high-earner rules. */
export const MAX_ANNUAL_SALARY = 999999;

export const PAY_FREQUENCY_ERROR = "Select how often you're paid";

export const HOURLY_WAGE_MIN_ERROR = 'Enter an hourly wage greater than $0';

export const HOURLY_WAGE_MAX_ERROR = `Enter an hourly wage up to $${MAX_HOURLY_WAGE.toLocaleString('en-US')}`;

export const ANNUAL_SALARY_MIN_ERROR = 'Enter a salary greater than $0';

export const ANNUAL_SALARY_MAX_ERROR = `Enter a salary up to $${MAX_ANNUAL_SALARY.toLocaleString('en-US')}`;

export const WEEKLY_HOURS_ERROR = `Enter hours greater than 0, up to ${MAX_WEEKLY_HOURS}`;

export const OVERTIME_WAGE_MIN_ERROR = 'Enter an overtime wage of $0 or more';

export const OVERTIME_WAGE_MAX_ERROR = `Enter an overtime wage up to $${MAX_OVERTIME_WAGE.toLocaleString('en-US')}`;

export const OVERTIME_HOURS_ERROR = 'Enter overtime hours of 0 or more';

export const TOTAL_WEEKLY_HOURS_ERROR = `Regular and overtime hours can total up to ${MAX_WEEKLY_HOURS} a week`;

export const YES_NO_OPTIONS: readonly YesNoOption[] = [
  { key: 'yes', label: 'Yes' },
  { key: 'no', label: 'No' },
];

export const EXEMPTION_QUESTIONS: readonly ExemptionQuestion[] = [
  { key: 'federal', legend: 'Are you exempt from Federal Income Tax Withholding?', hint: PLACEHOLDER },
  { key: 'socialSecurity', legend: 'Are you exempt from Social Security Tax?', hint: PLACEHOLDER },
  { key: 'medicare', legend: 'Are you exempt from Medicare Tax?', hint: PLACEHOLDER },
];

export const NO_EXEMPTIONS: PaycheckExemptions = { federal: false, socialSecurity: false, medicare: false };

export const INITIAL_CURRENCY_VALUES: CalculatorFieldValues = {
  [HOURLY_WAGE_FIELD.label]: '',
  [ANNUAL_SALARY_FIELD.label]: '',
  [OVERTIME_WAGE_FIELD.label]: '',
};

export const INITIAL_HOURS_VALUES: CalculatorFieldValues = {
  [WEEKLY_HOURS_FIELD.label]: '',
  [OVERTIME_HOURS_FIELD.label]: '',
};

/** Lowercased because it only ever appears mid-sentence, as in "Est. bi-weekly take-home pay". */
export const frequencyPhrase = (key: PayFrequency): string => getPayFrequency(key).label.toLowerCase();

/** Gross leads because every other line is carved out of it. */
export const RESULT_ROWS = [
  { key: 'gross', label: (frequency: string) => `Gross ${frequency} income` },
  { key: 'takeHome', label: () => 'Take-home pay' },
  { key: 'federal', label: () => 'Federal income tax' },
  { key: 'socialSecurity', label: () => 'Social Security' },
  { key: 'medicare', label: () => 'Medicare' },
] as const;

/** Clockwise from 12 o'clock in the legend's row order, so the ring and the table read in step. */
export const SEGMENT_ORDER: readonly PaycheckSegmentKey[] = ['takeHome', 'federal', 'socialSecurity', 'medicare'];

/**
 * Text colours: the donut strokes with `currentColor` and the legend swatch fills with `bg-current`,
 * so one class colours both and the two cannot drift.
 */
export const SEGMENT_COLORS: Record<PaycheckSegmentKey, string> = {
  takeHome: 'text-accent-green-400',
  federal: 'text-accent-green-600',
  socialSecurity: 'text-accent-green-500',
  medicare: 'text-accent-green-300',
};

export const RESULT_COPY = {
  eyebrow: (frequency: string) => `Est. ${frequency} take-home pay`,
} as const;

/** Self-contained copy for the live-region announcement; the panel itself is too long to hear out. */
export const SUMMARY_COPY = {
  takeHome: (frequency: string, takeHome: string, gross: string) =>
    `Your estimated ${frequency} take-home pay is ${takeHome}, out of ${gross} gross income.`,
} as const;
